import { Box, IconButton, Typography, Radio, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import React, { useEffect, useState } from 'react';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import { formatDate } from '../../../../utils/date';
import { useNotification } from '../../../../hooks/useNotification';
import TextField from '../../../common/TextField';
import EditIcon from '../../../common/icons/EditIcon';
import DatePickerField from '../../../common/DatePickerField';
import dayjs from 'dayjs';
import Button from '../../../common/Button';
import Notification from '../../../common/Notification';
import api from '../../../../services/httpClient';
import { APPLICANT_ENDPOINTS } from '../../../../services/endpoints/rec/applicant.endpoints';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import { useParams } from 'react-router-dom';
import Loading from '../../../common/Loading';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';

interface ApplicantQuestion {
  id: number;
  vacancyQuestionId: number;
  question: string;
  questionLevel: 0 | 1 | 5;
  vacancyId: number;
  answerType: 'text' | 'number' | 'decimal' | 'boolean' | 'date' | 'currency';
  answerText: string | null;
  answerNumber: number | null;
  answerDecimal: number | null;
  answerCurrency: number | null;
  answerBoolean: boolean | null;
  answerDate: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string | null;
}

interface QuestionAnswersResponse {
  applicantId: number;
  questions: ApplicantQuestion[];
}

interface AdditionalInfoProps {
  setSummary?: (summary: ApplicantProfileData['basicInfo']) => void;
}

const QUESTION_LEVELS: Array<{ level: 0 | 1 | 5; label: string }> = [
  { level: 0, label: 'Interview Questions' },
  { level: 1, label: 'First Interview' },
  { level: 5, label: 'Final Interview' },
];

// Helper function to format number with thousand separators
const formatNumberWithCommas = (value: string | number | null | undefined): string => {
  if (!value && value !== 0) return '';
  const numStr = value.toString().replace(/,/g, '');
  if (numStr === '' || isNaN(Number(numStr))) return '';
  const num = parseFloat(numStr);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Helper function to remove commas and get numeric value
const removeCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

const toNonNegativeNumber = (value: string): number => {
  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);
};

const toNullableNonNegativeNumber = (value: string): number | null =>
  value === '' ? null : toNonNegativeNumber(value);

const salaryInputText = (value: number | null | undefined): string =>
  value == null ? '' : String(value);

const toNonNegativeText = (value: string): string => {
  if (value === '') {
    return '';
  }
  return toNonNegativeNumber(value).toString();
};

const toNonNegativeNullableNumber = (value: number | null): number | null => {
  return value === null ? null : Math.max(0, value);
};

const sanitizeQuestionAnswer = (question: ApplicantQuestion): ApplicantQuestion => ({
  ...question,
  answerNumber: toNonNegativeNullableNumber(question.answerNumber),
  answerDecimal: toNonNegativeNullableNumber(question.answerDecimal),
  answerCurrency: toNonNegativeNullableNumber(question.answerCurrency),
});

const isVietnam = (countryName: string): boolean =>
  /viet\s*nam/i.test(countryName);

const resolveSalaryCurrency = async (vacancyCode: string, applicantCountry?: string): Promise<string> => {
  if (!vacancyCode) {
    // If no vacancy, check applicant. If no applicant, default to USD.
    return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
  }

  try {
    const vacancyListResponse = await api.get<{ vacancies: { id: number; vacancyName: string; vacancyCode: string }[] }>(VACANCY_ENDPOINTS.list);
    const vacancies = vacancyListResponse.data.vacancies || [];
    const matchingVacancy = vacancies.find(v => v.vacancyCode === vacancyCode);

    if (!matchingVacancy) {
      return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
    }

    const vacancyResponse = await api.get<{
      locations?: Array<{
        country?: { id: number; name: string };
      }>;
    }>(
      VACANCY_ENDPOINTS.byId(matchingVacancy.id),
      {
        headers: { ...API_HEADERS.VACANCY_FORM_DEFAULT },
      }
    );

    const locations = vacancyResponse.data.locations || [];
    const currencies = new Set<string>();

    for (const location of locations) {
      if (location.country?.name) {
        currencies.add(isVietnam(location.country.name) ? 'VND' : 'USD');
      }
    }

    if (currencies.size === 1) {
      return Array.from(currencies)[0];
    }

    // Mixed vacancy (both Vietnam and non-Vietnam locations): use the applicant's assigned country
    if (applicantCountry) {
      return isVietnam(applicantCountry) ? 'VND' : 'USD';
    }
    // Ultimate fallback if both vacancy and applicant country are missing
    return 'USD';
  } catch (error) {
    console.error('Error fetching vacancy/currency data:', error);
    return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
  }
};

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({ setSummary }) => {
  const { id } = useParams();
  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditFirst, setIsEditFirst] = useState(false);
  const [isEditSecond, setIsEditSecond] = useState(false);
  const { pageId, setPageId, hasPermission } = usePermissions();
  const [values, setValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const [initialValues, setInitialValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const { notification, hideNotification, showNotification } = useNotification();
  const [applicantQuestions, setApplicantQuestions] = useState<{
    0: ApplicantQuestion[];
    1: ApplicantQuestion[];
    5: ApplicantQuestion[];
  }>({
    0: [],
    1: [],
    5: [],
  });
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [salaryCurrency, setSalaryCurrency] = useState<string>('');
  const [editingQuestionIds, setEditingQuestionIds] = useState<Set<number>>(new Set());
  const [draftAnswers, setDraftAnswers] = useState<{ 0: ApplicantQuestion[]; 1: ApplicantQuestion[]; 5: ApplicantQuestion[] }>({ 0: [], 1: [], 5: [] });
  const [savingQuestionId, setSavingQuestionId] = useState<number | null>(null);
  const [currentSalaryInput, setCurrentSalaryInput] = useState<string>('');
  const [expectedSalaryInput, setExpectedSalaryInput] = useState<string>('');

  useEffect(() => {
    if (!id || pageId !== PAGE_IDS.APPLICANT_ADDITIONAL_INFO) {
      setPageId(PAGE_IDS.APPLICANT_ADDITIONAL_INFO);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchApplicantData = async () => {
      const response = await api.get<{ data: ApplicantProfileData['basicInfo'] }>(APPLICANT_ENDPOINTS.basic(id),
        {
          headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_BASIC_GET },
        }
      );
      const basicData = response.data.data;
      const currency = await resolveSalaryCurrency(basicData.vacancyCode || '', basicData.vacancyCountry);
      setSalaryCurrency(currency);
      setValues(basicData);
      setInitialValues(basicData);
      if (setSummary) {
        setSummary(basicData);
      }
      setIsLoading(false);
    };

    fetchApplicantData();
  }, [id, refresh, setSummary, pageId, setPageId]);

  useEffect(() => {
    if (isEditFirst && values) {
      setCurrentSalaryInput(salaryInputText(values.currentSalary));
      setExpectedSalaryInput(salaryInputText(values.expectedSalary));
    }
    // Intentionally only re-run when edit mode toggles on; including `values` would
    // overwrite in-progress user input on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFirst]);

  // Separate useEffect for fetching questions - only depends on id
  useEffect(() => {
    if (!id) return;

    const fetchApplicantQuestions = async () => {
      setIsLoadingQuestions(true);
      try {
        const [level0Response, level1Response, level5Response] = await Promise.all([
          api.get<QuestionAnswersResponse>(
            APPLICANT_ENDPOINTS.questionAnswers(id, 0),
            { headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_QUESTION_ANSWERS } }
          ),
          api.get<QuestionAnswersResponse>(
            APPLICANT_ENDPOINTS.questionAnswers(id, 1),
            { headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_QUESTION_ANSWERS } }
          ),
          api.get<QuestionAnswersResponse>(
            APPLICANT_ENDPOINTS.questionAnswers(id, 5),
            { headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_QUESTION_ANSWERS } }
          ),
        ]);

        const grouped: { 0: ApplicantQuestion[]; 1: ApplicantQuestion[]; 5: ApplicantQuestion[] } = {
          0: (level0Response.data.questions || []).map(sanitizeQuestionAnswer),
          1: (level1Response.data.questions || []).map(sanitizeQuestionAnswer),
          5: (level5Response.data.questions || []).map(sanitizeQuestionAnswer),
        };

        ([0, 1, 5] as const).forEach((level) => {
          grouped[level].sort((a, b) => a.sortOrder - b.sortOrder);
        });

        setApplicantQuestions(grouped);
        setDraftAnswers(JSON.parse(JSON.stringify(grouped)));
      } catch (error: any) {
        console.error('Error fetching applicant questions:', error);
      } finally {
        setIsLoadingQuestions(false);
      }
    };

    fetchApplicantQuestions();
  }, [id]);

  if (isLoading || !values) {
    return <Loading />;
  }

  const salaryFields = [
    { 
      key: 'currentSalary',
      label: salaryCurrency ? `Current Salary (${salaryCurrency})` : 'Current Salary', 
      value: values?.currentSalary != null ? `${salaryCurrency ? salaryCurrency + ' ' : ''}${values.currentSalary.toLocaleString()}` : 'N/A', 
      width: 'calc(25% - 12px)' 
    },
    { 
      key: 'expectedSalary',
      label: salaryCurrency ? `Expected Salary (${salaryCurrency})` : 'Expected Salary', 
      value: values?.expectedSalary != null ? `${salaryCurrency ? salaryCurrency + ' ' : ''}${values.expectedSalary.toLocaleString()}` : 'N/A', 
      width: 'calc(25% - 12px)' 
    },
  ];

  const joiningFields = [
    { label: 'Notice Period', value: values?.noticePeriod ? `${values.noticePeriod} Days` : 'N/A', width: 'calc(25% - 12px)' },
    { label: 'Joining Date', value: values?.joiningDate ? formatDate(values.joiningDate) : 'N/A', width: 'calc(25% - 12px)' },
    { label: 'Joining Note', value: values?.joiningNote ? values.joiningNote : 'N/A', width: 'calc(25% - 12px)' },
  ];

  const handleSaveChanges = async () => {
    const applicantId = id;
    if (applicantId === undefined) {
      return;
    }
    if (values.joiningDate && !dayjs(values.joiningDate).isValid()) {
      showNotification('Please enter a valid joining date', 'error');
      return;
    }
    try {
      const payload = {
        currentSalary: values.currentSalary,
        expectedSalary: values.expectedSalary,
        noticePeriod: values.noticePeriod,
        joiningDate: values.joiningDate,
        joiningNote: values.joiningNote,
      }
      setIsSaving(true);
      await api.put(APPLICANT_ENDPOINTS.updateAdditional(applicantId), payload,
        {
          headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_UPDATE },
        }
      );

      showNotification('Changes saved successfully', 'success');
      setRefresh(refresh + 1);
    } catch (error) {
      showNotification('Error saving changes', 'error');
      console.error('Error saving changes:', error);
    } finally {
      setIsEditFirst(false);
      setIsEditSecond(false);
      setIsSaving(false);
    }
  };

  const handleAnswerChange = (questionId: number, field: string, value: any) => {
    setDraftAnswers((prev) => {
      const updated = { ...prev };
      ([0, 1, 5] as const).forEach((level) => {
        updated[level] = prev[level].map((q) =>
          q.id === questionId ? { ...q, [field]: value } as ApplicantQuestion : q
        );
      });
      return updated;
    });
  };

  const handleSaveAnswer = async (questionId: number, level: 0 | 1 | 5) => {
    const applicantId = id;
    if (applicantId === undefined) {
      return;
    }
    const q = draftAnswers[level].find((q) => q.id === questionId);
    if (!q) return;
    if (q.answerType === 'date' && q.answerDate && !dayjs(q.answerDate).isValid()) {
      showNotification('Please enter a valid date', 'error');
      return;
    }
    try {
      setSavingQuestionId(questionId);

      await api.put(APPLICANT_ENDPOINTS.applicantAnswers(applicantId), {
        id: q.id,
        answerType: q.answerType,
        answerText: q.answerType === 'text' ? q.answerText : null,
        answerNumber: q.answerType === 'number' ? toNonNegativeNullableNumber(q.answerNumber) : null,
        answerDecimal: q.answerType === 'decimal' ? toNonNegativeNullableNumber(q.answerDecimal) : null,
        answerCurrency: q.answerType === 'currency' ? toNonNegativeNullableNumber(q.answerCurrency) : null,
        answerBoolean: q.answerType === 'boolean' ? q.answerBoolean : null,
        answerDate: q.answerType === 'date' ? (q.answerDate || null) : null,
      }, {
        headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_SAVE_ANSWER },
      });

      // Update local state with saved value
      const updatedApplicantQuestions = {
        ...applicantQuestions,
        [level]: applicantQuestions[level].map((existing) =>
          existing.id === questionId ? { ...JSON.parse(JSON.stringify(q)) } : existing
        ),
      };
      setApplicantQuestions(updatedApplicantQuestions);
      
      // Update draft answers, preserving other questions' draft states
      setDraftAnswers((prev) => {
        const updated = { ...prev };
        updated[level] = prev[level].map((existing) =>
          existing.id === questionId ? { ...JSON.parse(JSON.stringify(q)) } : existing
        );
        return updated;
      });
      
      // Only remove the saved question from editing state, keep others in edit mode
      setEditingQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
      showNotification('Changes saved successfully', 'success');
    } catch (error) {
      showNotification('Error saving changes', 'error');
      console.error('Error saving answer:', error);
    } finally {
      setSavingQuestionId(null);
    }
  };

  const enterEditQuestion = (id: number) => {
    setEditingQuestionIds((prev) => new Set(prev).add(id));
  };

  const discardQuestion = (id: number, level: 0 | 1 | 5) => {
    setDraftAnswers((prev) => ({
      ...prev,
      [level]: prev[level].map((q) =>
        q.id === id ? { ...JSON.parse(JSON.stringify(applicantQuestions[level].find((s) => s.id === id) || q)) } : q
      ),
    }));
    setEditingQuestionIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const renderAnswer = (question: ApplicantQuestion, isEditing: boolean) => {
    switch (question.answerType) {
      case 'text':
        return (
          <TextField
            multiline
            value={question.answerText ?? ''}
            onChange={(e) => handleAnswerChange(question.id, 'answerText', e.target.value)}
            disabled={!isEditing}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '8px 12px',
                minHeight: '38px',
                height: 'auto',
                alignItems: 'flex-start',
                backgroundColor: '#fff',
              },
              '& .MuiOutlinedInput-input': {
                padding: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'Inter',
                fontSize: '15px',
                fontWeight: 500,
                color: '#000000',
                textAlign: 'justify',
              },
            }}
          />
        );
      case 'number':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton
              onClick={() => handleAnswerChange(question.id, 'answerNumber', Math.max(0, (question.answerNumber ?? 0) - 1))}
              disabled={!isEditing || (question.answerNumber ?? 0) <= 0}
              sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid #702DFF', p: 0, '&:hover': { backgroundColor: '#F1F3FF' }, '&.Mui-disabled': { borderColor: '#D0D5DD' } }}
            >
              <RemoveIcon sx={{ fontSize: 16, color: isEditing ? '#702DFF' : '#D0D5DD' }} />
            </IconButton>
            <TextField
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={question.answerNumber ?? ''}
              onChange={(e) => handleAnswerChange(question.id, 'answerNumber', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10)))}
              onBlur={(e) => {
                if (e.target.value === '') {
                  handleAnswerChange(question.id, 'answerNumber', 0);
                }
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              sx={{
                width: 60,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#702DFF' },
                  '&:hover fieldset': { borderColor: '#702DFF' },
                  '&.Mui-focused fieldset': { borderColor: '#702DFF' },
                  '&.Mui-disabled fieldset': { borderColor: '#D0D5DD' },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '4px 8px',
                  textAlign: 'center',
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' },
                  MozAppearance: 'textfield',
                },
              }}
            />
            <IconButton
              onClick={() => handleAnswerChange(question.id, 'answerNumber', (question.answerNumber ?? 0) + 1)}
              disabled={!isEditing}
              sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid #702DFF', p: 0, '&:hover': { backgroundColor: '#F1F3FF' }, '&.Mui-disabled': { borderColor: '#D0D5DD' } }}
            >
              <AddIcon sx={{ fontSize: 16, color: isEditing ? '#702DFF' : '#D0D5DD' }} />
            </IconButton>
          </Box>
        );
      case 'decimal':
        return (
          <Box sx={{ width: '200px' }}>
            <TextField
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={question.answerDecimal ?? ''}
              onChange={(e) => handleAnswerChange(question.id, 'answerDecimal', e.target.value === '' ? null : toNonNegativeNumber(e.target.value))}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                },
              }}
            />
          </Box>
        );
      case 'currency':
        return (
          <Box sx={{ width: '200px' }}>
            <TextField
              type="text"
              value={formatNumberWithCommas(question.answerCurrency)}
              onChange={(e) => {
                const rawValue = removeCommas(e.target.value);
                if (rawValue === '' || /^[0-9]*\.?[0-9]*$/.test(rawValue)) {
                  handleAnswerChange(question.id, 'answerCurrency', rawValue === '' ? null : parseFloat(rawValue));
                }
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              slotProps={{ input: { startAdornment: <InputAdornment position="start">{salaryCurrency || 'USD'}</InputAdornment> } }}
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                },
              }}
            />
          </Box>
        );
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: -1, height: '38px' }}>
            <Radio
              checked={question.answerBoolean === true}
              onChange={() => isEditing && handleAnswerChange(question.id, 'answerBoolean', true)}
              disabled={!isEditing}
              sx={{ color: '#D0D5DD', '&.Mui-checked': { color: '#702DFF' }, p: '6px' }}
            />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', fontWeight: 500, color: '#000000', ml: -1 }}>Yes</Typography>
            <Radio
              checked={question.answerBoolean === false}
              onChange={() => isEditing && handleAnswerChange(question.id, 'answerBoolean', false)}
              disabled={!isEditing}
              sx={{ color: '#D0D5DD', '&.Mui-checked': { color: '#702DFF' }, p: '6px' }}
            />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', fontWeight: 500, color: '#000000', ml: -1 }}>No</Typography>
          </Box>
        );
      case 'date':
        return (
          <Box sx={{ width: '200px' }}>
            <DatePickerField
              value={question.answerDate ? dayjs(question.answerDate) : null}
              onChange={(date: string | null) => handleAnswerChange(question.id, 'answerDate', date || null)}
              disabled={!isEditing}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditFirst && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditFirst(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {salaryFields.map((field) => (
              <Box key={field.key} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label}</Typography>
                {
                  isEditFirst ? (
                    <>
                      {field.key === 'currentSalary' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={currentSalaryInput}
                          onChange={(e) => {
                            setCurrentSalaryInput(e.target.value);
                            setValues({ ...values, currentSalary: toNullableNonNegativeNumber(e.target.value) });
                          }}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                      {field.key === 'expectedSalary' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={expectedSalaryInput}
                          onChange={(e) => {
                            setExpectedSalaryInput(e.target.value);
                            setValues({ ...values, expectedSalary: toNullableNonNegativeNumber(e.target.value) });
                          }}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>
                }
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditSecond && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditSecond(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {joiningFields.map((field) => (
              <Box key={field.label} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label} {(isEditSecond && field.label === 'Notice Period') ? ' (in days)' : ''}</Typography>
                {
                  isEditSecond ? (
                    <>
                      {field.label === 'Notice Period' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={values.noticePeriod}
                          onChange={(e) => setValues({ ...values, noticePeriod: toNonNegativeText(e.target.value) })}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                      {field.label === 'Joining Date' && (
                        <DatePickerField
                          value={values.joiningDate ? dayjs(values.joiningDate) : null}
                          onChange={(date: string | null) => setValues({ ...values, joiningDate: date ? date : '' })}
                        />
                      )}
                      {field.label === 'Joining Note' && (
                        <TextField
                          value={values.joiningNote}
                          onChange={(e) => setValues({ ...values, joiningNote: e.target.value as string })}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>
                }
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      {(isEditFirst || isEditSecond) && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1246px', mt: 1 }}>
          <Button
            variant="outlined"
            disabled={isSaving}
            className="edit-section-cancel-button"
            onClick={() => {
              setIsEditFirst(false);
              setIsEditSecond(false);
              setValues(initialValues);
              setCurrentSalaryInput(salaryInputText(initialValues?.currentSalary));
              setExpectedSalaryInput(salaryInputText(initialValues?.expectedSalary));
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            isLoading={isSaving}
            disabled={isSaving}
            onClick={handleSaveChanges}
            className="edit-section-save-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Interview Questions Section */}
      {isLoadingQuestions ? (
        <Box className="view-section">
          <Loading message="Loading interview questions..." />
        </Box>
      ) : (applicantQuestions[0].length === 0 && applicantQuestions[1].length === 0 && applicantQuestions[5].length === 0) ? (
        <Box className="view-section">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <div className="view-section-title">Interview Questions</div>
          </Box>
          <Typography sx={{ color: '#667085', fontSize: '14px' }}>
            No interview questions allocated for this applicant.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <div className="view-section-title">Interview Questions</div>
          </Box>

          {QUESTION_LEVELS.filter(({ level }) => draftAnswers[level].length > 0).map(({ level, label }) => (
            <Box key={level} className="view-section">
              <div className="view-section-title" style={{ marginBottom: '12px', fontSize: '16px' }}>{label}</div>

              {draftAnswers[level].map((question, index) => {
                const isEditing = editingQuestionIds.has(question.id);

                // Find the original question to compare values
                const originalQuestion = applicantQuestions[level].find((q) => q.id === question.id);
                
                // Determine if the answer has changed
                const hasChanges = (() => {
                  if (!originalQuestion) return false;
                  switch (question.answerType) {
                    case 'text':
                      // Handle null vs empty string equivalency if needed, or strict comparison
                      return (question.answerText ?? '') !== (originalQuestion.answerText ?? '');
                    case 'number':
                      return question.answerNumber !== originalQuestion.answerNumber;
                    case 'decimal':
                      return question.answerDecimal !== originalQuestion.answerDecimal;
                    case 'currency':
                      return question.answerCurrency !== originalQuestion.answerCurrency;
                    case 'boolean':
                      return question.answerBoolean !== originalQuestion.answerBoolean;
                    case 'date': {
                      if (!question.answerDate && !originalQuestion.answerDate) return false; // If both are empty/null, no changes
                      if (!question.answerDate || !originalQuestion.answerDate) return true;  // If only one is empty/null, it changed
                      return !dayjs(question.answerDate).isSame(dayjs(originalQuestion.answerDate), 'day'); // Compare the actual dates, ignoring time/string format differences
                    }
                    default:
                      return false;
                  }
                })();

                return (
                  <Box key={question.id} sx={{ backgroundColor: '#F1F3FF', borderRadius: '8px', padding: '16px 16px', mb: 2 }}>
                    {/* Question row */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 0.5 }}>
                      <Box sx={{ width: 24, height: 24, minWidth: 24, borderRadius: '50%', backgroundColor: '#702DFF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 500 }}>
                        {index + 1}
                      </Box>
                      <Typography className="view-label-title" sx={{ flex: 1, textAlign: 'justify !important', pt: '4px', textTransform: 'none !important' }}>
                        {question.question}
                      </Typography>
                    </Box>
                    {/* Answer row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 5 }}>
                      <Box sx={{ flex: 1 }}>
                        {renderAnswer(question, isEditing)}
                      </Box>
                      {hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && !isEditing && (
                        <IconButton onClick={() => enterEditQuestion(question.id)} sx={{ p: '4px' }}>
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                    {/* Save/Discard buttons below answer */}
                    {isEditing && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, ml: 5 }}>
                        <Button
                          variant="outlined"
                          className="edit-section-cancel-button"
                          disabled={savingQuestionId === question.id}
                          onClick={() => discardQuestion(question.id, level)}
                          sx={{ minWidth: 'auto', padding: '4px 12px', fontSize: '13px', height: '30px' }}
                        >
                          Discard
                        </Button>
                        <Button
                          variant="contained"
                          className="edit-section-save-button"
                          disabled={savingQuestionId === question.id || !hasChanges} // Disable if currently saving or no changes to save
                          isLoading={savingQuestionId === question.id}
                          onClick={() => handleSaveAnswer(question.id, level)}
                          sx={{ minWidth: 'auto', padding: '4px 12px', fontSize: '13px', height: '30px' }}
                        >
                          Save
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}

            </Box>
          ))}
        </>
      )}

      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default AdditionalInfo; 